import React, { useEffect, useMemo, useState, useContext } from "react";
import axiosInstance from "../../axiosInstance";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../Spinner";
import Table from "../Theme/Table.js";
import ActionButtons from "../ActionButton.js";
import Chip from "@mui/material/Chip";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { RoleContext } from "../RoleContext";

const TripRequestDetail = ({ socket }) => {
  const { id } = useParams();
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [cancelDialog, setCancelDialog] = useState({ open: false });
  const [cancelReason, setCancelReason] = useState("");

  const canCancel = role?.some((e) => e.perm_name === "list_active_trips");

  const fetchDetails = async () => {
    try {
      const res = await axiosInstance.get(`/tripRequests/${id}`);
      setData(res.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error("Failed to load trip request");
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const tripRequest = data?.tripRequest || null;
  const emergencyGroupId = data?.emergencyGroupId || null;
  const groupItems = data?.groupItems || [];

  const handleCancel = async () => {
    try {
      if (!cancelReason || cancelReason.trim() === "") {
        toast.error("Please provide a reason for cancelling this trip");
        return;
      }

      await axiosInstance.post("/acceptedTripRequests/cancel", {
        emergencyGroupId: emergencyGroupId || undefined,
        tripRequestId: emergencyGroupId ? undefined : tripRequest?.id,
        reasonForCancellation: cancelReason.trim(),
      });

      if (socket && socket.connected) {
        socket.emit("broadcastCancellation", {
          emergencyGroupId: emergencyGroupId || undefined,
          tripRequestId: emergencyGroupId ? undefined : tripRequest?.id,
          reasonForCancellation: cancelReason.trim(),
        });
      }

      toast.success("Cancelled");
      setCancelDialog({ open: false });
      setCancelReason("");
      setLoading(true);
      await fetchDetails();
    } catch (e) {
      toast.error("Failed to cancel");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Trip Requests",
        columns: [
          { Header: "ID", accessor: "id" },
          { Header: "Status", accessor: "status" },
          { Header: "Driver ID", accessor: "driverId" },
          { Header: "Passenger ID", accessor: "passengerId" },
        ],
      },
    ],
    []
  );

  return (
    <div className="content-wrappers" style={{ marginTop: "-40px" }}>
      <div className="content-wrappers">
        <br />
        <br />
        <ToastContainer />

        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <ActionButtons title="Trip Request Detail" is_all_trips={false} />
                  <div className="card-body">
                    {loading ? (
                      <Spinner />
                    ) : !tripRequest ? (
                      <Typography>Not found</Typography>
                    ) : (
                      <>
                        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
                          <Chip
                            label={tripRequest.status || "N/A"}
                            color={tripRequest.status === "Accepted" ? "info" : tripRequest.status === "Cancelled" ? "error" : "default"}
                          />
                          {emergencyGroupId && (
                            <Typography sx={{ fontSize: "0.9rem", color: "#374151" }}>
                              Emergency Group: {emergencyGroupId}
                            </Typography>
                          )}
                          {canCancel && (tripRequest.status === "Accepted" || tripRequest.status === "Active") && (
                            <button
                              onClick={() => {
                                setCancelDialog({ open: true });
                                setCancelReason("");
                              }}
                              style={{
                                backgroundColor: "#b91c1c",
                                color: "white",
                                padding: "8px 16px",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "500",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </Box>

                        <Table columns={columns} data={groupItems.length > 0 ? groupItems : [tripRequest]} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Dialog
          open={!!cancelDialog.open}
          onClose={() => {
            setCancelDialog({ open: false });
            setCancelReason("");
          }}
          PaperProps={{
            style: {
              borderRadius: "12px",
              padding: "16px",
              maxWidth: "450px",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              pb: 1,
              "& .MuiTypography-root": {
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#b91c1c",
              },
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <WarningIcon sx={{ color: "#b91c1c", fontSize: 28 }} />
              <Typography>Cancel Trip Request</Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pb: 3 }}>
            <Typography align="center" sx={{ fontSize: "1rem", color: "#4b5563", mt: 1, mb: 2 }}>
              This will cancel {emergencyGroupId ? "the whole emergency group" : "the trip request"}.
            </Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: "600", mb: 1, color: "#374151" }}>
                Reason for Cancellation <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <textarea
                placeholder="Reason..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: cancelReason.trim() ? "2px solid #10b981" : "2px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "0.95rem",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
              {!cancelReason.trim() && (
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: 0.5 }}>This field is required</Typography>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              padding: "16px",
              borderTop: "1px solid #e5e7eb",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              onClick={() => {
                setCancelDialog({ open: false });
                setCancelReason("");
              }}
              sx={{
                color: "#6b7280",
                backgroundColor: "#f3f4f6",
                padding: "8px 24px",
                "&:hover": { backgroundColor: "#e5e7eb" },
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleCancel}
              sx={{
                backgroundColor: "#b91c1c",
                color: "white",
                padding: "8px 24px",
                "&:hover": { backgroundColor: "#991b1b" },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default TripRequestDetail;
